import { TrustAiBoardControllerService as Board } from "@/api";
import type {
  AuditEvidenceRecord,
  ConnectPlatformParams,
  ControlCategory,
  ControlDecision,
  EnforcePolicyParams,
  EnforcementResult,
  EnterprisePosture,
  EstateReadiness,
  EstateReadinessParams,
  IncidentAdvisory,
  VerifiedOutcomes,
  WhatsChanged,
} from "@/api/temp/TrustAIExecutiveService";
import type { QueryMethod, RequestOptions } from "@/hooks/usePaginateQuery";

/** Retypes a generated `(params, options)` method; the function is kept, so `method.name` stays the mock key. */
const typed = <Args, Response>(method: unknown) =>
  method as QueryMethod<Args, Response>;

/**
 * The generated TrustAI Board APIs, typed as the UI's models: every generated
 * field is optional and its unions are plain strings.
 *
 * The generated no-param methods take `options` first, but the hooks call
 * `method(params, { signal })`, so those get adapters that pass the signal
 * through. They are shorthand methods, not function declarations: a method's
 * `name` is its property key, which the production minifier keeps, while it
 * renames plain functions - and `method.name` is the mock and query key.
 */
export const TrustAiBoardApi = {
  getEnterprisePostureUsingGet(_: unknown, options?: RequestOptions) {
    return Board.getEnterprisePostureUsingGet(
      options,
    ) as unknown as Promise<EnterprisePosture>;
  },
  listPriorityControlsUsingGet(_: unknown, options?: RequestOptions) {
    return Board.listPriorityControlsUsingGet(options) as unknown as Promise<
      ControlCategory[]
    >;
  },
  // `scenario` is the demo's state switcher; the live API takes no params.
  getEstateReadinessUsingGet(
    _: EstateReadinessParams,
    options?: RequestOptions,
  ) {
    return Board.getEstateReadinessUsingGet(
      options,
    ) as unknown as Promise<EstateReadiness>;
  },
  startEvaluationUsingPost() {
    return Board.startEvaluationUsingPost() as unknown as Promise<EstateReadiness>;
  },
  startOnboardingRunUsingPost: typed<
    { body: ConnectPlatformParams },
    EstateReadiness
  >(Board.startOnboardingRunUsingPost),
  getControlDecisionUsingGet: typed<
    { controlId: string },
    ControlDecision | undefined
  >(Board.getControlDecisionUsingGet),
  getVerifiedOutcomesUsingGet: typed<{ controlId: string }, VerifiedOutcomes>(
    Board.getVerifiedOutcomesUsingGet,
  ),
  listAuditEvidenceUsingGet(_: unknown, options?: RequestOptions) {
    return Board.listAuditEvidenceUsingGet(options) as unknown as Promise<
      AuditEvidenceRecord[]
    >;
  },
  enforcePolicyUsingPost: typed<
    { controlId: string; body: Omit<EnforcePolicyParams, "controlId"> },
    EnforcementResult
  >(Board.enforcePolicyUsingPost),
  listRelevantIntelligenceUsingGet(_: unknown, options?: RequestOptions) {
    return Board.listRelevantIntelligenceUsingGet(
      options,
    ) as unknown as Promise<IncidentAdvisory[]>;
  },
  getWhatsChangedUsingGet(_: unknown, options?: RequestOptions) {
    return Board.getWhatsChangedUsingGet(
      options,
    ) as unknown as Promise<WhatsChanged>;
  },
};
